// PR-31: top-level React error boundary.
//
// Why this exists: React 19 isolates render-time errors from
// `window.onerror` — a thrown error inside render / lifecycle / hook
// effects unmounts the tree without firing the global handler.
// Without a boundary the user sees a blank page and our central
// error monitor never learns about it. This one wraps <App />, calls
// `reportClientError` with the React component stack (the missing
// piece you can't get from a normal stack trace), and shows a
// reload-prompt fallback so the user has a way out.
//
// Single root-level boundary on purpose: per-route boundaries are a
// later refinement. For now we'd rather catch every render error
// (even in unusual surfaces like the mobile shell or the admin
// errors page) than miss some by under-wrapping.
//
// Async errors — Promises, setTimeout callbacks — DON'T reach this
// boundary; they go through `unhandledrejection` (already handled in
// `error_capture.ts`). The boundary only catches synchronous render
// /lifecycle/effect-cleanup errors, which is exactly the gap.

import { Component, type ReactNode } from 'react'
import { reportClientError } from './error_capture'

interface State {
  /** Stash the error so the fallback UI can show its message
   *  (handy in dev / when the user is willing to paste it back). */
  error: Error | null
}

interface Props {
  children: ReactNode
}

export class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    reportClientError(error, {
      action: 'react.render',
      // The component stack is the unique value-add of an error
      // boundary — it's NOT in `error.stack` and tells you which
      // component subtree threw. Without this, post-mortem on
      // "Uncaught TypeError: Cannot read x of undefined" is just a
      // minified bundle line number.
      component_stack: info.componentStack ?? null,
    })
  }

  render() {
    if (this.state.error) {
      return (
        <div
          role="alert"
          style={{
            padding: 24,
            fontFamily: 'system-ui, sans-serif',
            maxWidth: 480,
            margin: '64px auto',
            color: '#1a1a1a',
          }}
        >
          <h2 style={{ marginTop: 0 }}>出错了</h2>
          <p style={{ color: '#666' }}>
            页面遇到了无法恢复的错误。错误已自动上报。
          </p>
          <pre
            style={{
              background: '#f5f5f5',
              padding: 12,
              borderRadius: 6,
              fontSize: 12,
              overflow: 'auto',
              maxHeight: 160,
            }}
          >
            {this.state.error.message}
          </pre>
          <button
            type="button"
            onClick={() => {
              window.location.reload()
            }}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: '1px solid #d0d0d0',
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            刷新页面
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
